/*
 * Authors: mariushirjanu
 * Created: 31/08/2021
 */
import { util } from '../../../shared/util';
import { Injectable } from '@angular/core';
import { ICodeValue, ICompanyExtended, IContact, ICountryExtended, IDescribedObject, ILocationExtended, INamedObject} from '../../../../type_definitions/datatypes';
import { CompanyDataService } from '../../../shared/company/company-data.service';
import { TranslationService } from '../../../shared/translation/translation.service';

export interface ICompanyData {
  id?:number;
  name:string;
  type:ICodeValue;
  group:any;
  gaSkin:IDescribedObject;
  gbdm:IContact;
  comment:string;
  hideGeneralFaqItems?:boolean;
  address:string;
  zipCode:string;
  city:string;
  country:ICountryExtended;
  companyHeadquartersId:number;
  companyHeadquarters?:ILocationExtended;
  vatNumber:string;
  vatName:string;
  urlExternalTicket:string;
  allowChatWithACSL:boolean;
  allowChatWithOwnGroup:boolean;
  allowChatWithOwnCompany:boolean;
  allowChatWithBP:boolean;
  allowChatWithCatalogOwners:boolean;
  hasSubCompanies:boolean;
  subCompanyMandatory:boolean;
  subCompanyLabel:string;
  welcomeText:string;
  ledger:INamedObject;
  active:boolean;
}

@Injectable()
export class CompanyInfoService {
  public static NEW_COMPANY:string = 'NEW';

  private companyId:number;
  private companyProfileData:ICompanyData;
  private companyProfilePromise:Promise<ICompanyExtended>;

  constructor(
    private companyDataService:CompanyDataService,
    private translationService:TranslationService
  ) {/**/
  }

  // set the promise for the selected company (companyId specified in url parameter)
  public setCompanyId(companyId:string):void {
    this.companyProfilePromise = Promise.resolve(<any>{});

    if (companyId === CompanyInfoService.NEW_COMPANY || !companyId) {
      // when new company 'active' field should have default value -> true
      this.setCompanyData(<any>{ active: true });
    } else if (companyId) {
      this.companyId = Number(companyId);

      // reload the company data
      this.companyProfilePromise = this.companyDataService.getCompany(this.companyId)
        .then((cd:ICompanyExtended) => {
          this.setCompanyData(cd);
          return cd;
        });
    }
  }

  public getCompanyId():number {
    return this.companyId;
  }

  public setCompanyData(companyProfile:ICompanyExtended):void {
    this.companyId = companyProfile.id;
    this.companyProfilePromise = Promise.resolve(companyProfile);
    this.companyProfileData = {
      id: companyProfile.id,
      name: companyProfile.name,
      type: companyProfile && companyProfile.companyType? <any>{ id: companyProfile.companyType } : undefined,
      group: companyProfile.companyGroup,
      gaSkin: companyProfile.gaLayout,
      gbdm: companyProfile.gbdm,
      comment: companyProfile.comment,
      urlExternalTicket: companyProfile.urlExternalTicket,
      address: companyProfile.address,
      zipCode: companyProfile.zipCode,
      city: companyProfile.city,
      country: companyProfile.country,
      vatNumber: companyProfile.vatNumber,
      vatName: companyProfile.country && companyProfile.country.vatName? companyProfile.country.vatName : this.translationService.instant('GA.GENERAL.VATTAX'),
      companyHeadquartersId: companyProfile.companyHeadquartersId,
      allowChatWithACSL: companyProfile.allowChatWithACSL,
      allowChatWithOwnGroup: companyProfile.allowChatWithOwnGroup,
      allowChatWithOwnCompany: companyProfile.allowChatWithOwnCompany,
      allowChatWithBP: companyProfile.allowChatWithBP,
      allowChatWithCatalogOwners: companyProfile.allowChatWithCatalogOwners,
      hideGeneralFaqItems: util.notNullOrUndefined(companyProfile.showOnlyCompanyFaqItems)? !companyProfile.showOnlyCompanyFaqItems : undefined,
      hasSubCompanies: companyProfile.hasSubCompanies,
      subCompanyMandatory: companyProfile.subCompanyMandatory,
      subCompanyLabel: companyProfile.subCompanyLabel,
      welcomeText: companyProfile.welcomeText,
      ledger: companyProfile.ledger,
      active: companyProfile.active
    };
  }

  public getCompanyProfile():ICompanyData {
    return this.companyProfileData;
  }

  public createCompanyProfileRequest():ICompanyExtended {
    const companyProfileRequest = <any>{
      id: this.companyProfileData.id,
      name: this.companyProfileData.name,
      companyType: this.companyProfileData.type && this.companyProfileData.type.id,
      companyGroup: this.companyProfileData.group,
      gaLayout: this.companyProfileData.gaSkin,
      gbdm: this.companyProfileData.gbdm,
      comment: this.companyProfileData.comment,
      companyHeadquartersId: this.companyProfileData.companyHeadquartersId,
      allowChatWithACSL: this.companyProfileData.allowChatWithACSL,
      allowChatWithOwnGroup: this.companyProfileData.allowChatWithOwnGroup,
      allowChatWithOwnCompany: this.companyProfileData.allowChatWithOwnCompany,
      allowChatWithBP: this.companyProfileData.allowChatWithBP,
      allowChatWithCatalogOwners: this.companyProfileData.allowChatWithCatalogOwners,
      showOnlyCompanyFaqItems: !this.companyProfileData.hideGeneralFaqItems,
      hasSubCompanies: this.companyProfileData.hasSubCompanies,
      subCompanyMandatory: this.companyProfileData.subCompanyMandatory,
      subCompanyLabel: this.companyProfileData.subCompanyLabel,
      welcomeText: this.companyProfileData.welcomeText,
      active: this.companyProfileData.active
    };

    if (this.companyProfileData.ledger) {
      companyProfileRequest.ledger = this.companyProfileData.ledger;
    }

    if (!this.companyProfileData.companyHeadquartersId) {
      companyProfileRequest.country = this.companyProfileData.country;
      companyProfileRequest.address = this.companyProfileData.address;
      companyProfileRequest.zipCode = this.companyProfileData.zipCode;
      companyProfileRequest.city = this.companyProfileData.city;
      companyProfileRequest.vatNumber = this.companyProfileData.vatNumber;
    }

    return companyProfileRequest;
  }

  public getCompanyProfilePromise():Promise<ICompanyExtended> {
    return this.companyProfilePromise;
  }

  public clear():void {
    this.companyId = undefined;
    this.companyProfilePromise = undefined;
    this.companyProfileData = undefined;
  }
}